import React, { useEffect, useState } from "react";
import { fournisseurService } from "../../../../services/fournisseurService";
import { userService } from "../../../../services/userService";
import styles from "./EnvoyerAuFinanceurModal.module.scss";

/**
 * LS-657 (addendum) — modale de génération de l'accord de financement,
 * calquée sur EnvoyerAuFinanceurModal (LS-625) : champs à gauche, aperçu du
 * contenu du PDF à droite. La fidélité visuelle de l'aperçu importe moins
 * que son contenu — il reprend le tableau de véhicules qui sera effectivement
 * généré côté serveur (une ligne par devis de la demande), pas une maquette
 * du PDF lui-même.
 *
 * LS-689 — `tampons` : ceux de l'utilisateur connecté (il ne peut choisir que
 * les siens, l'API rejette le reste en 422). Le choix est optionnel et n'est
 * **jamais présélectionné** — « Aucun tampon » à chaque ouverture, y compris
 * quand l'utilisateur n'en a qu'un : c'est une garantie métier contre les
 * erreurs de cachet, pas un oubli d'ergonomie. Le champ est masqué s'il n'a
 * aucun tampon, la génération restant possible.
 */
const GenererAccordModal = ({
  isOpen,
  lignes,
  tampons = [],
  onSubmit,
  onClose,
}) => {
  const [fournisseurUuid, setFournisseurUuid] = useState("");
  const [tamponUuid, setTamponUuid] = useState("");
  // Review sprint20 — les URLs des tampons du store périment (signées 30 min
  // sur S3) : la liste est rechargée à chaque ouverture, la prop ne servant
  // que d'état initial et de repli si le rechargement échoue.
  const [tamponsAffiches, setTamponsAffiches] = useState(tampons);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setIsPreparing(true);
    setFournisseurUuid("");
    setTamponUuid("");
    setTamponsAffiches(tampons);

    userService
      .getUtilisateurConnecte()
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.tampons)) {
          setTamponsAffiches(res.data.tampons);
        }
      })
      .catch(() => {});

    fournisseurService
      .getAllFournisseurs()
      .then((res) => {
        if (cancelled) return;
        setFournisseurs(Array.isArray(res.data) ? res.data : res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFournisseurs([]);
      })
      .finally(() => {
        if (!cancelled) setIsPreparing(false);
      });

    return () => {
      cancelled = true;
    };
    // `tampons` est volontairement hors dépendances : la prop ne sert que
    // d'état initial au moment de l'ouverture, le rechargement fait le reste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedFournisseur = fournisseurs.find((f) => f.uuid === fournisseurUuid);
  const selectedTampon = tamponsAffiches.find((t) => t.uuid === tamponUuid);
  const isValid = fournisseurUuid !== "";
  const busy = isSubmitting;
  const quantiteTotale = lignes.reduce((sum, l) => sum + (Number(l.quantite) || 0), 0);

  const handleCancel = () => {
    if (busy) return;
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleCancel();
  };

  const handleSubmit = async () => {
    if (!isValid || busy) return;
    setIsSubmitting(true);
    const ok = await onSubmit(fournisseurUuid, selectedFournisseur, tamponUuid || null);
    setIsSubmitting(false);
    if (ok) onClose();
  };

  return (
    <div
      className={`modal d-block ${styles.backdrop}`}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generer-accord-title"
      onClick={handleCancel}
      onKeyDown={handleKeyDown}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`modal-content ${styles.content}`}>
          <div className={styles.header}>
            <div className={styles.headerIcon} aria-hidden="true">
              <i className="fa fa-file-pdf-o" />
            </div>
            <div className={styles.headerText}>
              <h5 className="modal-title" id="generer-accord-title">
                Générer l'accord de financement
              </h5>
              <small>Un PDF conforme au template BPCE sera attaché à la demande.</small>
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleCancel}
              aria-label="Fermer"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className={styles.body}>
            {isPreparing ? (
              <div className={styles.loading}>
                <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                Préparation…
              </div>
            ) : (
              <>
                <div className={styles.fieldsCol}>
                  <div className={styles.field}>
                    <label htmlFor="generer-accord-fournisseur">
                      Fournisseur destinataire
                    </label>
                    <select
                      id="generer-accord-fournisseur"
                      className="form-control"
                      value={fournisseurUuid}
                      onChange={(e) => setFournisseurUuid(e.target.value)}
                      disabled={busy}
                    >
                      <option value="">— Sélectionner un fournisseur —</option>
                      {fournisseurs.map((f) => (
                        <option key={f.uuid} value={f.uuid}>
                          {f.nom}
                        </option>
                      ))}
                    </select>
                    <p className={styles.hintText}>
                      Ses coordonnées sont insérées dans le document.
                    </p>
                  </div>

                  {tamponsAffiches.length > 0 && (
                    <div className={styles.field}>
                      <label htmlFor="generer-accord-tampon">Tampon</label>
                      <select
                        id="generer-accord-tampon"
                        className="form-control"
                        value={tamponUuid}
                        onChange={(e) => setTamponUuid(e.target.value)}
                        disabled={busy}
                      >
                        <option value="">— Aucun tampon —</option>
                        {tamponsAffiches.map((t) => (
                          <option key={t.uuid} value={t.uuid}>
                            {t.nom}
                          </option>
                        ))}
                      </select>
                      <p className={styles.hintText}>
                        Apposé sur le PDF généré. Aucun tampon n'est
                        présélectionné.
                      </p>
                    </div>
                  )}
                </div>

                <div className={styles.previewCol}>
                  <p className={styles.colLabel}>Contenu du document</p>
                  <div className={styles.mailCard}>
                    <div
                      className={`${styles.mailFournisseur} ${
                        selectedFournisseur ? "" : styles.mailFournisseurEmpty
                      }`}
                    >
                      <strong>FOURNISSEUR</strong>
                      {selectedFournisseur ? (
                        <>
                          <span>{selectedFournisseur.nom}</span>
                          {selectedFournisseur.description && (
                            <span>{selectedFournisseur.description}</span>
                          )}
                        </>
                      ) : (
                        <span>Aucun fournisseur sélectionné</span>
                      )}
                    </div>
                    <div className={styles.mailBody}>
                      <table className="table table-sm table-bordered mb-2">
                        <thead>
                          <tr>
                            <th>Véhicule</th>
                            <th>Qté</th>
                            <th>HT unit.</th>
                            <th>TTC unit.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lignes.length === 0 ? (
                            <tr>
                              <td colSpan={4}>Aucun devis rattaché à la demande.</td>
                            </tr>
                          ) : (
                            lignes.map((l, i) => (
                              <tr key={i}>
                                <td>{l.vehicule || "—"}</td>
                                <td>{l.quantite}</td>
                                <td>
                                  {Number(l.montantHtUnitaire || 0).toLocaleString("fr-FR")} €
                                </td>
                                <td>
                                  {Number(l.montantTtcUnitaire || 0).toLocaleString("fr-FR")} €
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      <p style={{ marginBottom: 0 }}>
                        Total : {quantiteTotale} véhicule{quantiteTotale > 1 ? "s" : ""}
                      </p>
                      {selectedTampon && (
                        <div style={{ marginTop: 12, textAlign: "right" }}>
                          <img
                            src={selectedTampon.url}
                            alt={`Tampon ${selectedTampon.nom}`}
                            style={{ maxHeight: 70, maxWidth: 140 }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles.footer}>
            <p className={styles.footerHint}>
              {!isPreparing && !isValid ? "Sélectionnez un fournisseur pour générer." : ""}
            </p>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={handleCancel}
              disabled={busy}
            >
              Annuler
            </button>
            <button
              type="button"
              className={styles.btnSend}
              onClick={handleSubmit}
              disabled={busy || !isValid || isPreparing}
            >
              <i
                className={`fa fa-${busy ? "spinner fa-spin" : "file-pdf-o"}`}
                aria-hidden="true"
              />
              {busy ? "Génération..." : "Générer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenererAccordModal;
